import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** The logged-in user as kept in localStorage */
export interface CurrentUser {
	/** Supabase auth id */
	id: string;
	/** Database id */
	userId: number;
	email: string;
	/** "Name LastName", ready to show */
	displayName: string;
	role: { id: number; name: string; description?: string | null } | null;
	permissions: string[];
	enabled: boolean;
	emailVerified: boolean;
	profilePhotoUrl: string | null;
}

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
	private readonly USER_KEY = 'currentUser';

	constructor(@Inject(PLATFORM_ID) private platformId: object) {}

	private currentUserSubject = new BehaviorSubject<CurrentUser | null>(this.getCurrentUser());
	public currentUser$: Observable<CurrentUser | null> = this.currentUserSubject.asObservable();

	/**
	 * Returns the active user.
	 */
	getCurrentUser(): CurrentUser | null {
		if (!isPlatformBrowser(this.platformId)) {
			return null;
		}

		const userStr = localStorage.getItem(this.USER_KEY);
		if (!userStr) return null;

		try {
			const user = JSON.parse(userStr);
			// Sessions saved before the rename keep the name under the old Spanish key
			if (!user.displayName && user.nombre) user.displayName = user.nombre;
			return user as CurrentUser;
		} catch (error) {
			console.error('Error parsing user data:', error);
			return null;
		}
	}

	/**
	 * Returns the Supabase ID of the active user.
	 */
	getId(): string | undefined {
		const user = this.getCurrentUser();
		return user?.id;
	}

	/**
	 * Returns the database user ID of the active user.
	 */
	getUserId(): number | undefined {
		const user = this.getCurrentUser();
		return user?.userId;
	}

	/**
	 * Returns the general email of the active user.
	 */
	getEmail(): string | undefined {
		const user = this.getCurrentUser();
		return user?.email;
	}

	/**
	 * Updates the active user.
	 */
	setCurrentUser(userData: CurrentUser): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		// Ensure we have the minimum required data
		const user: CurrentUser = {
			id: userData.id, // Supabase ID
			userId: userData.userId, // Database ID
			email: userData.email,
			displayName: userData.displayName,
			role: userData.role || null, // Single role object
			permissions: userData.permissions || [], // Array of permission names
			enabled: userData.enabled,
			emailVerified: userData.emailVerified,
			profilePhotoUrl: userData.profilePhotoUrl || null, // Cached profile photo URL
		};

		localStorage.setItem(this.USER_KEY, JSON.stringify(user));
		this.currentUserSubject.next(user);
	}

	/**
	 * Returns the profile photo URL of the active user.
	 */
	getProfilePhotoUrl(): string | null {
		const user = this.getCurrentUser();
		return user?.profilePhotoUrl || null;
	}

	/**
	 * Updates only the profile photo URL without affecting other user data.
	 */
	updateProfilePhotoUrl(url: string | null): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		const user = this.getCurrentUser();
		if (user) {
			user.profilePhotoUrl = url;
			localStorage.setItem(this.USER_KEY, JSON.stringify(user));
			this.currentUserSubject.next(user);
		}
	}

	/**
	 * Clears the active user from localStorage (logout).
	 */
	clearCurrentUser(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		localStorage.removeItem(this.USER_KEY);
		this.currentUserSubject.next(null);
	}

	/**
	 * Returns the role of the active user.
	 */
	getUserRole(): CurrentUser['role'] {
		const user = this.getCurrentUser();
		return user?.role || null;
	}

	/**
	 * Returns the role name of the active user.
	 */
	getUserRoleName(): string | null {
		const user = this.getCurrentUser();
		return user?.role?.name || null;
	}

	/**
	 * Returns all roles of the active user (as an array for compatibility).
	 */
	getUserRoles(): string[] {
		const roleName = this.getUserRoleName();
		return roleName ? [roleName] : [];
	}

	/**
	 * Checks if the user has a specific role
	 */
	hasRole(roleToCheck: string): boolean {
		const user = this.getCurrentUser();
		return user?.role?.name?.toLowerCase() === roleToCheck.toLowerCase() || false;
	}

	/**
	 * Checks if the user has any of the specified roles
	 */
	hasAnyRole(rolesToCheck: string[]): boolean {
		const user = this.getCurrentUser();
		const userRole = user?.role?.name?.toLowerCase();
		return rolesToCheck.some((role) => role.toLowerCase() === userRole) || false;
	}

	/**
	 * Returns the user's base type (always employee)
	 */
	getBaseType(): 'employee' | null {
		const _user = this.getCurrentUser();
		return 'employee';
	}

	/**
	 * Returns all permissions of the active user
	 */
	getPermissions(): string[] {
		const user = this.getCurrentUser();
		return user?.permissions || [];
	}

	/**
	 * Checks if the user has a specific permission
	 */
	hasPermission(permissionToCheck: string): boolean {
		const permissions = this.getPermissions();
		return permissions.includes(permissionToCheck);
	}

	/**
	 * For the permissions that replaced role checks (orders:create-direct, requests:approve...): a session
	 * started before they existed only gets them in the stored user at the next token refresh. Admins
	 * already had these actions (and the backend grants them the permission), so their role still counts
	 * meanwhile; any other role needs the permission itself.
	 */
	hasPermissionOrAdmin(permissionToCheck: string): boolean {
		return this.hasPermission(permissionToCheck) || this.hasRole('admin');
	}

	/**
	 * Checks if the user has any of the specified permissions
	 */
	hasAnyPermission(permissionsToCheck: string[]): boolean {
		const userPermissions = this.getPermissions();
		return permissionsToCheck.some((permission) => userPermissions.includes(permission));
	}

	/**
	 * Checks if the user has all of the specified permissions
	 */
	hasAllPermissions(permissionsToCheck: string[]): boolean {
		const userPermissions = this.getPermissions();
		return permissionsToCheck.every((permission) => userPermissions.includes(permission));
	}
}
